import { Button, Group, Slider, Space, Text, Title } from "@mantine/core";
import * as React from "react";
import { MetronomeBarCounter } from "@/components/game-player/components/metronome-bar-counter";
import { theHandyDevice } from "@/integrated-devices/thehandy/thehandy-stroker-device";
import { useDebouncedValue } from "@mantine/hooks";
import { autoblowDevice } from "@/integrated-devices/autoblow/autoblow-stroker-device";
import { createFileRoute } from "@tanstack/react-router";
import { Page } from "@/components/page";
import { GameObjectManager } from "@/game2/game-object-manager";
import { BeatEmitter } from "@/game2/game-objects/beat-emitter";
import { generateNoteLabel, type Measure } from "@/utils/music";
import { DEFAULT_MEASURE, measures } from "@/utils/measures";

export const Route = createFileRoute(
  "/_authenticated/_subscribed/_main/metronome",
)({
  component: MetronomeRouteComponent,
});

function MetronomeRouteComponent() {
  const gameObjectManagerRef = React.useRef<GameObjectManager>(undefined);
  const [beatEmitter, setBeatEmitter] = React.useState<BeatEmitter>();
  const [tempo, setTempo] = React.useState(0);
  const [measure, setMeasure] = React.useState<Measure>(DEFAULT_MEASURE);
  const [debouncedTempo] = useDebouncedValue(tempo, 500);
  const [paused, setPaused] = React.useState(false);

  React.useEffect(() => {
    const controller = new AbortController();
    const gameObjectManager = new GameObjectManager();

    gameObjectManager.onPause(() => {
      setPaused(true);
    }, controller.signal);

    gameObjectManager.onUnpause(() => {
      setPaused(false);
    }, controller.signal);

    const beatEmitter = new BeatEmitter();
    gameObjectManager.add(beatEmitter);

    beatEmitter.onBeatQueued((beat) => {
      gameObjectManager.add(beat);
    }, controller.signal);

    beatEmitter.onTempoChange((tempo) => {
      setTempo(tempo);
    }, controller.signal);

    beatEmitter.onMeasureChange((measure) => {
      setMeasure(measure);
    }, controller.signal);

    gameObjectManagerRef.current = gameObjectManager;

    setBeatEmitter(beatEmitter);
    setMeasure(beatEmitter.measure);

    gameObjectManager.start();

    return () => {
      gameObjectManager.destroy();
      controller.abort();
    };
  }, []);

  React.useEffect(() => {
    if (theHandyDevice.connected) {
      theHandyDevice.tempo(debouncedTempo);
    }
    if (autoblowDevice.connected) {
      autoblowDevice.tempo(debouncedTempo);
    }

    return () => {
      if (theHandyDevice.connected) {
        theHandyDevice.tempo(0);
      }
      if (autoblowDevice.connected) {
        autoblowDevice.tempo(0);
      }
    };
  }, [debouncedTempo]);

  if (!beatEmitter) {
    return;
  }

  return (
    <Page title="Metronome">
      <Group>
        <Title>Metronome</Title>
        <Button
          radius="xl"
          onClick={() => {
            paused
              ? gameObjectManagerRef.current?.unpause()
              : gameObjectManagerRef.current?.pause();
          }}
        >
          {paused ? "Unpause" : "Pause"}
        </Button>
      </Group>
      <Text>Tempo:</Text>
      <Button.Group>
        {[0, 60, 120, 300].map((t) => (
          <Button
            key={`tempo-${t}`}
            variant={t === tempo ? "primary" : "default"}
            onClick={() => {
              beatEmitter.setTempo(t);
            }}
          >
            {t}
          </Button>
        ))}
      </Button.Group>
      <Space h="1rem" />
      <Text>Measure:</Text>
      <Button.Group>
        {measures.map((m) => {
          const label = generateNoteLabel(m.notes);
          return (
            <Button
              key={`measure-${label}`}
              variant={m === measure ? "primary" : "default"}
              onClick={() => {
                beatEmitter.setMeasure(m);
              }}
            >
              {label}
            </Button>
          );
        })}
      </Button.Group>
      <Text span style={{ fontSize: "4rem" }}>
        {tempo}
      </Text>
      <Text span tt="uppercase">
        BPM
      </Text>
      <Text>
        Measure:
        {generateNoteLabel(measure.notes)}
      </Text>
      <Slider
        size="xl"
        min={0}
        max={300}
        marks={[
          { value: 0, label: "0" },
          { value: 60, label: "60" },
          { value: 120, label: "120" },
          { value: 180, label: "180" },
          { value: 240, label: "240" },
          { value: 320, label: "300" },
        ]}
        value={tempo}
        onChange={(value) => {
          beatEmitter.setTempo(value);
        }}
      />
      <Space h={50} />
      <MetronomeBarCounter beatEmitter={beatEmitter} />
    </Page>
  );
}
